/* globals jQuery */
/**
 * Miscellaneous utilities.
 * @module utilities
 * @license MIT
 *
 * @copyright 2010 Alexis Deveria, 2010 Jeff Schiller
 */

import jQueryPluginSVG from './jQuery.attr'; // Needed for SVG attribute setting and array form with `attr`
import { NS } from '../common/namespaces';
import { getTransformList } from './svgtransformlist';
import { setUnitAttr, getSnappingTypeMap } from '../common/units';
import { hasMatrixTransform, transformListToTransform, transformBox } from './math';
import { supportsHVLineContainerBBox, isSafari } from '../common/browser';
import { getClosest } from '../editor/jgraduate/Util.js';
import { getCanvasDpi } from '../common/pdUtils';
import { BatchCommand, RemoveElementCommand, InsertElementCommand } from './undocommands';
import SvgCanvas from './svgcanvas';

// Constants
const $ = jQueryPluginSVG(jQuery);

// String used to encode base64.
const KEYSTR = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

// Much faster than running getBBox() every time
const visElems = 'a,circle,ellipse,foreignObject,g,image,line,path,polygon,polyline,rect,svg,text,tspan,use,clipPath';
const visElemsArr = visElems.split(',');

let svgCanvas: SvgCanvas = null;
let domdoc_: Document = null;
let domcontainer_: HTMLElement = null;
let svgroot_: SVGSVGElement = null;

/**
* Object with the following keys/values.
* @typedef {PlainObject} module:utilities.SVGElementJSON
* @property {string} element - Tag name of the SVG element to create
* @property {PlainObject<string, string>} attr - Has key-value attributes to assign to the new element. An `id` should be set so that {@link module:utilities.EditorContext#addSVGElementFromJson} can later re-identify the element for modification or replacement.
* @property {boolean} [curStyles=false] - Indicates whether current style attributes should be applied first
* @property {module:utilities.SVGElementJSON[]} [children] - Data objects to be added recursively as children
* @property {string} [namespace="http://www.w3.org/2000/svg"] - Indicate a (non-SVG) namespace
*/

/**
* @function module:utilities.init
* @param {module:utilities.EditorContext} editorContext
* @returns {void}
*/
export const init = function (canvas: SvgCanvas) {
    svgCanvas = canvas;
    domdoc_ = svgCanvas.getDOMDocument();
    domcontainer_ = svgCanvas.getDOMDocumentContainer();
    svgroot_ = canvas.getSVGRoot();
};

/**
 * Used to prevent the [Billion laughs attack]{@link https://en.wikipedia.org/wiki/Billion_laughs_attack}.
 * @function module:utilities.dropXMLInternalSubset
 * @param {string} str String to be processed
 * @returns {string} The string with entity declarations in the internal subset removed
 * @todo This might be needed in other places `parseFromString` is used even without LGTM flagging
 */
export const dropXMLInternalSubset = (str: string): string => {
    return str.replace(/(<!DOCTYPE\s+\w*\s*\[).*(\?]>)/, '$1$2');
};

/**
* Converts characters in a string to XML-friendly entities.
* @function module:utilities.toXml
* @example `&` becomes `&amp;`
* @param {string} str - The string to be converted
* @returns {string} The converted string
*/
export const toXml = function (str: string): string {
    // &apos; is ok in XML, but not HTML
    // &gt; does not normally need escaping, though it can if within a CDATA expression (and preceded by "]]")
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;'); // Note: `&apos;` is XML only
};

/**
* Converts XML entities in a string to single characters.
* @function module:utilities.fromXml
* @example `&amp;` becomes `&`
* @param {string} str - The string to be converted
* @returns {string} The converted string
*/
export function fromXml(str: string): string {
    const p = document.createElement('p');
    // eslint-disable-next-line no-unsanitized/property
    p.innerHTML = str;
    return p.textContent;
}

// This code was written by Tyler Akins and has been placed in the
// public domain.  It would be nice if you left this header intact.
// Base64 code from Tyler Akins -- http://rumkin.com

// schiller: Removed string concatenation in favour of Array.join() optimization,
//        also precalculate the size of the array needed.

/**
* Converts a string to base64.
* @function module:utilities.encode64
* @param {string} input
* @returns {string} Base64 output
*/
export function encode64(input: string): string {
    // base64 strings are 4/3 larger than the original string
    input = encodeUTF8(input); // convert non-ASCII characters
    // input = convertToXMLReferences(input);
    if (window.btoa) {
        return window.btoa(input); // Use native if available
    }
    const output = new Array(Math.floor((input.length + 2) / 3) * 4);

    let i = 0,
        p = 0;
    do {
        const chr1 = input.charCodeAt(i++);
        const chr2 = input.charCodeAt(i++);
        const chr3 = input.charCodeAt(i++);

        /* eslint-disable no-bitwise */
        const enc1 = chr1 >> 2;
        const enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);

        let enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
        let enc4 = chr3 & 63;
        /* eslint-enable no-bitwise */

        if (Number.isNaN(chr2)) {
            enc3 = 64;
            enc4 = 64;
        } else if (Number.isNaN(chr3)) {
            enc4 = 64;
        }

        output[p++] = KEYSTR.charAt(enc1);
        output[p++] = KEYSTR.charAt(enc2);
        output[p++] = KEYSTR.charAt(enc3);
        output[p++] = KEYSTR.charAt(enc4);
    } while (i < input.length);

    return output.join('');
}

/**
* Converts a string from base64.
* @function module:utilities.decode64
* @param {string} input Base64-encoded input
* @returns {string} Decoded output
*/
export function decode64(input: string): string {
    if (window.atob) {
        return decodeUTF8(window.atob(input));
    }

    // remove all characters that are not A-Z, a-z, 0-9, +, /, or =
    input = input.replace(/[^A-Za-z\d+/=]/g, '');

    let output = '';
    let i = 0;

    do {
        const enc1 = KEYSTR.indexOf(input.charAt(i++));
        const enc2 = KEYSTR.indexOf(input.charAt(i++));
        const enc3 = KEYSTR.indexOf(input.charAt(i++));
        const enc4 = KEYSTR.indexOf(input.charAt(i++));

        /* eslint-disable no-bitwise */
        const chr1 = (enc1 << 2) | (enc2 >> 4);
        const chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
        const chr3 = ((enc3 & 3) << 6) | enc4;
        /* eslint-enable no-bitwise */

        output += String.fromCharCode(chr1);

        if (enc3 !== 64) {
            output += String.fromCharCode(chr2);
        }
        if (enc4 !== 64) {
            output += String.fromCharCode(chr3);
        }
    } while (i < input.length);
    return decodeUTF8(output);
}

/**
* @function module:utilities.decodeUTF8
* @param {string} argString
* @returns {string}
*/
export function decodeUTF8(argString: string): string {
    return decodeURIComponent(escape(argString));
}

// codedread:does not seem to work with webkit-based browsers on OSX // Brettz9: please test again as function upgraded
/**
* @function module:utilities.encodeUTF8
* @param {string} argString
* @returns {string}
*/
export const encodeUTF8 = function (argString: string): string {
    return unescape(encodeURIComponent(argString));
};

/**
 * Convert dataURL to object URL.
 * @function module:utilities.dataURLToObjectURL
 * @param {string} dataurl
 * @returns {string} object URL or empty string
 */
export const dataURLToObjectURL = function (dataurl: string): string {
    if (typeof Uint8Array === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
        return '';
    }

    const arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = window.atob(arr[1]);

    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }

    const blob = new Blob([u8arr], { type: mime });
    return URL.createObjectURL(blob);
};

/**
 * Get object URL for a blob object.
 * @function module:utilities.createObjectURL
 * @param {Blob} blob A Blob object or File object
 * @returns {string} object URL or empty string
 */
export const createObjectURL = function (blob): string {
    if (!blob || typeof URL === 'undefined' || !URL.createObjectURL) {
        return '';
    }
    return URL.createObjectURL(blob);
};

/**
 * @property {string} blankPageObjectURL
 */
export const blankPageObjectURL = (function (): string {
    if (typeof Blob === 'undefined') {
        return '';
    }
    const blob = new Blob(['<html><head><title>SVG-edit</title></head><body>&nbsp;</body></html>'], { type: 'text/html' });
    return createObjectURL(blob);
})();

/**
* Converts a string to use XML references (for non-ASCII).
* @function module:utilities.convertToXMLReferences
* @param {string} input
* @returns {string} Decimal numeric character references
*/
export const convertToXMLReferences = function (input: string): string {
    let output = '';

    [...input].forEach((ch) => {
        const c = ch.charCodeAt(0);
        output += (c <= 127) ? ch : `&#${c};`;
    });

    return output;
};

/**
* Cross-browser compatible method of converting a string to an XML tree.
* Found this function [here]{@link http://groups.google.com/group/jquery-dev/browse_thread/thread/c6d11387c580a77f}.
* @function module:utilities.text2xml
* @param {string} sXML
* @throws {Error}
* @returns {XMLDocument}
*/
export const text2xml = function (sXML: string): XMLDocument {
    if (sXML.includes('<svg:svg')) {
        sXML = sXML.replace(/<(\/?)svg:/g, '<$1').replace('xmlns:svg', 'xmlns');
    }

    let out, dXML: DOMParser & { async: boolean, loadXML(string) };
    try {
        dXML = (window.DOMParser) ? new DOMParser() : new (<any>window).ActiveXObject('Microsoft.XMLDOM');
        dXML.async = false;
    } catch (e) {
        throw new Error('XML Parser could not be instantiated');
    }
    try {
        if (dXML.loadXML) {
            out = (dXML.loadXML(sXML)) ? dXML : false;
        } else {
            out = dXML.parseFromString(sXML, 'text/xml');
        }
    } catch (e2) {
        throw new Error('Error parsing XML string');
    }

    return out;
};

/**
* Cross-browser compatible method of converting an XML tree to a string.
*/
export const xml2text = function (xmlNode: Element, indent: Integer = 0) {
    if (!xmlNode) {
        return '';
    }

    const out: string[] = [];
    for (let i = 0; i < indent; i++) {
        out.push(' ');
    }

    out.push(`<${xmlNode.nodeName}`);

    const attributes = [...xmlNode.attributes];
    for (const attr of attributes) {
        const attrVal = toXml(attr.nodeValue); // Need to escape key attributes.
        out.push(` ${attr.nodeName}="${attrVal}"`);
    }

    if (xmlNode.hasChildNodes()) {
        let bOneLine = false;

        out.push('>');
        ++indent;
        for (const child of xmlNode.childNodes) {
            switch (child.nodeType) {
                case Node.ELEMENT_NODE: // element node
                    out.push('\n');
                    out.push(xml2text(child as Element, indent));
                    break;
                case Node.TEXT_NODE: { // text node
                    const str = child.nodeValue.replace(/(?:^\s+|\s+$)/g, '');
                    if (str !== '') {
                        bOneLine = true;
                        out.push(toXml(child.nodeValue));
                    }
                    break;
                } case Node.CDATA_SECTION_NODE: // cdata node
                    out.push('\n');
                    out.push(new Array(indent).join(' '));
                    out.push('<![CDATA[');
                    out.push(child.nodeValue);
                    out.push(']]>');
                    break;
                case Node.COMMENT_NODE: // comment
                    const commentNode = child as Comment;
                    out.push('\n');
                    out.push(new Array(indent).join(' '));
                    out.push('<!--');
                    out.push(commentNode.data);
                    out.push('-->');
                    break;
            }
        }
        --indent;
        if (!bOneLine) {
            out.push('\n');
            for (let i = 0; i < indent; i++) { out.push(' '); }
        }
        out.push(`</${xmlNode.nodeName}>`);

    } else {
        out.push('/>');
    }

    return out.join('');
}

/**
* @typedef {PlainObject} module:utilities.BBoxObject (like `DOMRect`)
* @property {Float} x
* @property {Float} y
* @property {Float} width
* @property {Float} height
*/
export interface BBoxObject {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
* Converts a `SVGRect` into an object.
* @function module:utilities.bboxToObj
* @param {SVGRect} bbox - a SVGRect
* @returns {module:utilities.BBoxObject} An object with properties names x, y, width, height.
*/
export const bboxToObj = function ({ x, y, width, height }: SVGRect): BBoxObject {
    return { x, y, width, height };
};

/**
* @callback module:utilities.TreeWalker
* @param {Element} elem - DOM element being traversed
* @returns {void}
*/
export type TreeWalker = (elem: Element) => void;

/**
* Walks the tree and executes the callback on each element in a top-down fashion.
* @function module:utilities.walkTree
* @param {Element} elem - DOM element to traverse
* @param {module:utilities.TreeWalker} cbFn - Callback function to run on each element
* @returns {void}
*/
export const walkTree = function (elem: Element, cbFn: TreeWalker) {
    if (elem && elem.nodeType === Node.ELEMENT_NODE) {
        cbFn(elem);

        let i = elem.childNodes.length;
        while (i--) {
            walkTree(elem.childNodes.item(i) as Element, cbFn);
        }
    }
};

/**
* Walks the tree and executes the callback on each element in a depth-first fashion.
* @function module:utilities.walkTreePost
* @todo Shouldn't this be calling walkTreePost?
* @param {Element} elem - DOM element to traverse
* @param {module:utilities.TreeWalker} cbFn - Callback function to run on each element
* @returns {void}
*/
export const walkTreePost = function (elem: Element, cbFn: TreeWalker) {
    if (elem && elem.nodeType === Node.ELEMENT_NODE) {
        let i = elem.childNodes.length;
        while (i--) {
            walkTree(elem.childNodes.item(i) as Element, cbFn);
        }

        cbFn(elem);
    }
};

/**
* Extracts the URL from the `url(...)` syntax of some attributes.
* Three variants:
*  - `<circle fill="url(someFile.svg#foo)" />`
*  - `<circle fill="url('someFile.svg#foo')" />`
*  - `<circle fill='url("someFile.svg#foo")' />`
* @function module:utilities.getUrlFromAttr
* @param {string} attrVal The attribute value as a string
* @returns {string} String with just the URL, like "someFile.svg#foo"
*/
export const getUrlFromAttr = function (attrVal: string): string {
    if (attrVal) {
        // url('#somegrad')
        if (attrVal.startsWith('url("')) {
            return attrVal.substring(5, attrVal.indexOf('"', 6));
        }
        // url('#somegrad')
        if (attrVal.startsWith("url('")) {
            return attrVal.substring(5, attrVal.indexOf("'", 6));
        }
        if (attrVal.startsWith('url(')) {
            return attrVal.substring(4, attrVal.indexOf(')'));
        }
    }
    return null;
};

/**
* @function module:utilities.getHref
* @param {Element} elem
* @returns {string} The given element's `href` or `xlink:href` value
*/
export let getHref = function (elem: Element): string {
    return elem.getAttribute('href') || elem.getAttributeNS(NS.XLINK, 'href');
};

/**
* Sets the given element's `xlink:href` value.
* @function module:utilities.setHref
* @param {Element} elem
* @param {string} val
* @returns {void}
*/
export let setHref = function (elem: Element, val: string) {
    elem.setAttribute('href', val);
    elem.setAttributeNS(NS.XLINK, 'xlink:href', val);
};

/**
* @function module:utilities.findDefs
* @returns {SVGDefsElement} The document's `<defs>` element, creating it first if necessary
*/
export const findDefs = function (): SVGDefsElement {
    const svgElement = svgCanvas.getSVGContent();
    let defs = svgElement.getElementsByTagNameNS(NS.SVG, 'defs') as HTMLCollectionOf<SVGDefsElement>;
    let def: SVGDefsElement;
    if (defs.length > 0) {
        def = defs[0];
    } else {
        def = svgElement.ownerDocument.createElementNS(NS.SVG, 'defs');
        if (svgElement.firstChild) {
            // first child is a comment, so call nextSibling
            svgElement.insertBefore(def, svgElement.firstChild.nextSibling);
            // svgElement.firstChild.nextSibling.before(defs); // Not safe
        } else {
            svgElement.append(def);
        }
    }
    return def;
};

// TODO(codedread): Consider moving the next to functions to bbox.js

/**
* Get correct BBox for a path in Webkit.
* Converted from code found [here]{@link http://blog.hackers-cafe.net/2009/06/how-to-calculate-bezier-curves-bounding.html}.
* @function module:utilities.getPathBBox
* @param {SVGPathElement} path - The path DOM element to get the BBox for
* @returns {module:utilities.BBoxObject} A BBox-like object
*/
export const getPathBBox = function (path: SVGPathElement): BBoxObject {
    const seglist = path.pathSegList;
    const tot = seglist.numberOfItems;

    const bounds = [[], []];
    const start = seglist.getItem(0) as any; // TODO: Fix as any
    let P0 = [start.x, start.y];

    const getCalc = function (j, P1, P2, P3) {
        return function (t) {
            return 1 - t ** 3 * P0[j] +
                3 * 1 - t ** 2 * t * P1[j] +
                3 * (1 - t) * t ** 2 * P2[j] +
                t ** 3 * P3[j];
        };
    };

    for (let i = 0; i < tot; i++) {
        const seg = seglist.getItem(i) as any; // TODO: Fix as any

        if (seg.x === undefined) { continue; }

        // Add actual points to limits
        bounds[0].push(P0[0]);
        bounds[1].push(P0[1]);

        if (seg.x1) {
            const P1 = [seg.x1, seg.y1],
                P2 = [seg.x2, seg.y2],
                P3 = [seg.x, seg.y];

            for (let j = 0; j < 2; j++) {
                const calc = getCalc(j, P1, P2, P3);

                const b = 6 * P0[j] - 12 * P1[j] + 6 * P2[j];
                const a = -3 * P0[j] + 9 * P1[j] - 9 * P2[j] + 3 * P3[j];
                const c = 3 * P1[j] - 3 * P0[j];

                if (a === 0) {
                    if (b === 0) { continue; }
                    const t = -c / b;
                    if (t > 0 && t < 1) {
                        bounds[j].push(calc(t));
                    }
                    continue;
                }
                const b2ac = b ** 2 - 4 * c * a;
                if (b2ac < 0) { continue; }
                const t1 = (-b + Math.sqrt(b2ac)) / (2 * a);
                if (t1 > 0 && t1 < 1) { bounds[j].push(calc(t1)); }
                const t2 = (-b - Math.sqrt(b2ac)) / (2 * a);
                if (t2 > 0 && t2 < 1) { bounds[j].push(calc(t2)); }
            }
            P0 = P3;
        } else {
            bounds[0].push(seg.x);
            bounds[1].push(seg.y);
        }
    }

    const x = Math.min.apply(null, bounds[0]);
    const w = Math.max.apply(null, bounds[0]) - x;
    const y = Math.min.apply(null, bounds[1]);
    const h = Math.max.apply(null, bounds[1]) - y;
    return {
        x,
        y,
        width: w,
        height: h
    };
};

/**
* Get the given/selected element's bounding box object, checking for
* horizontal/vertical lines (see issue 717)
* Note that performance is currently terrible, so some way to improve would
* be great.
* @param {SVGGraphicsElement} selected - Container or `<use>` DOM element
* @returns {DOMRect} Bounding box object
*/
function groupBBFix(selected: SVGGraphicsElement): DOMRect {
    if (supportsHVLineContainerBBox()) {
        try {
            return selected.getBBox();
        }
        catch (e) {
            /* empty */
        }
    }

    const ref = svgCanvas.getDataStorage().get(selected, 'ref');
    let matched = null;
    let ret, copy;

    if (ref) {
        let elements = [];
        Array.prototype.forEach.call(ref.children, function (el) {
            const elem = el.cloneNode(true);
            elem.setAttribute('visibility', 'hidden');
            svgroot_.appendChild(elem);
            copy.push(elem);
            if (['line', 'path'].indexOf(elem.tagName) !== -1) {
                elements.push(elem);
            }
        });
        matched = (elements.length) ? elements : null;
    } else {
        matched = selected.querySelectorAll('line, path');
    }

    let issue = false;
    if (matched.length) {
        Array.prototype.forEach.call(matched, function (match) {
            const bb = match.getBBox();
            if (!bb.width || !bb.height) {
                issue = true;
            }
        });
        if (issue) {
            const elems = ref ? copy : selected.children;
            ret = getStrokedBBox(elems);
        } else {
            ret = selected.getBBox();
        }
    } else {
        ret = selected.getBBox();
    }
    if (ref) {
        copy.remove();
    }
    return ret;
}

/**
* Get the given/selected element's bounding box object, convert it to be more
* usable when necessary.
* @function module:utilities.getBBox
* @param {Element} elem - Optional DOM element to get the BBox for
* @returns {module:utilities.BBoxObject} Bounding box object
*/
export const getBBox = function (elem: SVGGraphicsElement) {
    const selected = elem || svgCanvas.getSelectedElements()[0]
    if (elem.nodeType !== Node.ELEMENT_NODE) {
        return null
    }

    // getBBox can be obscenely slow. Cache if we can.
    if (selected['_cachedBBox'] && Date.now() - selected['_cachedBBoxTime'] > 1000) {
        //return selected['_cachedBBox'];
    }

    const elname = selected.nodeName;

    let ret = null
    switch (elname) {
        case 'text':
            if (selected.textContent === '') {
                selected.textContent = 'a'; // Some character needed for the selector to use.
                ret = selected.getBBox();
                selected.textContent = '';
            } else if (selected.getBBox) {
                ret = selected.getBBox();
            }
            break;
        case 'path':
        case 'g':
        case 'a':
            ret = selected.getBBox();
            break;

        case 'use': {
            const useEl = selected as SVGUseElement;
            let useRect: { x: number, y: number, width: number, height: number } = null;

            try {
                useRect = useEl.getBBox();

                if (isSafari()) {
                    // Hack for Safari's bbox not quite considering the X and Y attributes for no reason.
                    useRect.x += useEl.x.baseVal.value;
                    useRect.y += useEl.y.baseVal.value
                }
            } catch (err) {
                console.warn(err);
                // Firefox may throw as (NS_ERROR_FAILURE) when the <use> is not rendered; fall back to the referenced element.
                const href = useEl.getAttribute('href') || useEl.getAttributeNS(NS.XLINK, 'href');
                const refEl = href?.startsWith('#')
                    ? (useEl.ownerDocument.getElementById(href.substring(1)) as unknown as SVGGraphicsElement)
                    : null;
                const x = useEl.x.baseVal.value;
                const y = useEl.y.baseVal.value;

                let refRect: { x: number, y: number, width: number, height: number } = null;
                if (refEl && typeof refEl.getBBox === 'function') {
                    try {
                        refRect = refEl.getBBox();
                    } catch (e) {
                        refRect = null;
                    }
                }

                if (!refRect) {
                    const vb = (refEl as unknown as SVGSymbolElement)?.viewBox?.baseVal;
                    refRect = {
                        x: vb?.x ?? 0,
                        y: vb?.y ?? 0,
                        width: useEl.width.baseVal.value || vb?.width || 0,
                        height: useEl.height.baseVal.value || vb?.height || 0
                    };
                }

                useRect = { x: refRect.x + x, y: refRect.y + y, width: refRect.width, height: refRect.height };
            }

            ret = useRect;
            break;
        }

        default:

            if (!visElemsArr.includes(elname)) {
                break;
            }

            if (selected) {
                try {
                    ret = selected.getBBox()
                } catch (err) {
                    const tspanSel = selected as SVGTSpanElement;
                    // tspan (and textPath apparently) have no `getBBox` in Firefox: https://bugzilla.mozilla.org/show_bug.cgi?id=937268
                    // Re: Chrome returning bbox for containing text element, see: https://bugs.chromium.org/p/chromium/issues/detail?id=349835
                    const extent = tspanSel.getExtentOfChar(0); // pos+dimensions of the first glyph
                    const width = tspanSel.getComputedTextLength(); // width of the tspan

                    ret = {
                        x: extent.x,
                        y: extent.y,
                        width,
                        height: extent.height
                    }
                }
            } else {
                // Check if element is child of a foreignObject
                const fo = getClosest(selected.parentNode as Element, 'foreignObject') as SVGForeignObjectElement;
                if (fo.length && fo[0].getBBox) {
                    ret = fo[0].getBBox()
                }
            }
    }

    if (ret) {
        ret = bboxToObj(ret);
        selected['_cachedBBox'] = ret;
        selected['_cachedBBoxTime'] = Date.now();
    }

    // get the bounding box from the DOM (which is in that element's coordinate system)
    return ret
}

/**
* @typedef {GenericArray} module:utilities.PathSegmentArray
* @property {Integer} length 2
* @property {"M"|"L"|"C"|"Z"} 0
* @property {Float[]} 1
*/
export type PathSegmentArrayType = "M" | "L" | "C" | "Z";
export type PathSegmentArray = [PathSegmentArrayType, number[]];

/**
* Create a path 'd' attribute from path segments.
* Each segment is an array of the form: `[singleChar, [x,y, x,y, ...]]`
* @function module:utilities.getPathDFromSegments
* @param {module:utilities.PathSegmentArray[]} pathSegments - An array of path segments to be converted
* @returns {string} The converted path d attribute.
*/
export const getPathDFromSegments = function (pathSegments: PathSegmentArray[]): string {
    let d = '';

    $.each(pathSegments, function (j, [singleChar, pts]) {
        d += singleChar;
        for (let i = 0; i < pts.length; i += 2) {
            d += (pts[i] + ',' + pts[i + 1]) + ' ';
        }
    });

    return d;
};

/**
* Make a path 'd' attribute from a simple SVG element shape.
* @function module:utilities.getPathDFromElement
* @param {Element} elem - The element to be converted
* @returns {string} The path d attribute or `undefined` if the element type is unknown.
*/
export const getPathDFromElement = function (elem: SVGGraphicsElement): string {
    // Possibly the cubed root of 6, but 1.81 works best
    let num = 1.81;
    let d, rx: number, ry: number;
    switch (elem.tagName) {
        case 'ellipse':
        case 'circle': {
            rx = parseFloat(elem.getAttribute('rx'));
            ry = parseFloat(elem.getAttribute('ry'));
            const cx = parseFloat(elem.getAttribute('cx'));
            const cy = parseFloat(elem.getAttribute('cy'));
            if (elem.tagName === 'circle' && elem.hasAttribute('r')) {
                ry = parseFloat(elem.getAttribute('r'));
                rx = ry;
            }
            d = getPathDFromSegments([
                ['M', [(cx - rx), (cy)]],
                ['C', [(cx - rx), (cy - ry / num), (cx - rx / num), (cy - ry), (cx), (cy - ry)]],
                ['C', [(cx + rx / num), (cy - ry), (cx + rx), (cy - ry / num), (cx + rx), (cy)]],
                ['C', [(cx + rx), (cy + ry / num), (cx + rx / num), (cy + ry), (cx), (cy + ry)]],
                ['C', [(cx - rx / num), (cy + ry), (cx - rx), (cy + ry / num), (cx - rx), (cy)]],
                ['Z', []]
            ]);
            break;
        } case 'path':
            d = elem.getAttribute('d');
            break;
        case 'line': {
            const x1 = elem.getAttribute('x1');
            const y1 = elem.getAttribute('y1');
            const x2 = elem.getAttribute('x2');
            const y2 = elem.getAttribute('y2');
            d = 'M' + x1 + ',' + y1 + 'L' + x2 + ',' + y2;
        }
            break;
        case 'polyline':
            d = 'M' + elem.getAttribute('points');
            break;
        case 'polygon':
            d = 'M' + elem.getAttribute('points') + ' Z';
            break;
        case 'rect': {
            rx = parseFloat(elem.getAttribute('rx'));
            ry = parseFloat(elem.getAttribute('ry'));
            const b = elem.getBBox();
            const { x, y } = b,
                w = b.width,
                h = b.height;
            num = 4 - num; // Why? Because!

            d = (!rx && !ry)
                // Regular rect
                ? getPathDFromSegments([
                    ['M', [x, y]],
                    ['L', [x + w, y]],
                    ['L', [x + w, y + h]],
                    ['L', [x, y + h]],
                    ['L', [x, y]],
                    ['Z', []]
                ])
                : getPathDFromSegments([
                    ['M', [x, y + ry]],
                    ['C', [x, y + ry / num, x + rx / num, y, x + rx, y]],
                    ['L', [x + w - rx, y]],
                    ['C', [x + w - rx / num, y, x + w, y + ry / num, x + w, y + ry]],
                    ['L', [x + w, y + h - ry]],
                    ['C', [x + w, y + h - ry / num, x + w - rx / num, y + h, x + w - rx, y + h]],
                    ['L', [x + rx, y + h]],
                    ['C', [x + rx / num, y + h, x, y + h - ry / num, x, y + h - ry]],
                    ['L', [x, y + ry]],
                    ['Z', []]
                ]);
            break;
        } default:
            break;
    }

    return d;
};

/**
* Get a set of attributes from an element that is useful for convertToPath.
* @function module:utilities.getExtraAttributesForConvertToPath
* @param {Element} elem - The element to be probed
* @returns {PlainObject<"marker-start"|"marker-end"|"marker-mid"|"filter"|"clip-path", string>} An object with attributes.
*/
export const getExtraAttributesForConvertToPath = function (elem: Element) {
    const attrs = {};
    // TODO: make this list global so that we can properly maintain it
    // TODO: what about @transform, @clip-rule, @fill-rule, etc?
    ['marker-start', 'marker-end', 'marker-mid', 'filter', 'clip-path'].forEach(function (item) {
        const a = elem.getAttribute(item);
        if (a) {
            attrs[item] = a;
        }
    });
    return attrs;
};

/**
* Get the BBox of an element-as-path.
* @function module:utilities.getBBoxOfElementAsPath
* @param {SVGGraphicsElement} elem - The DOM element to be probed
* @param {module:utilities.EditorContext#addSVGElementFromJson} addSVGElementFromJson - Function to add the path element to the current layer. See canvas.addSVGElementFromJson
* @param {module:path.pathActions} pathActions - If a transform exists, `pathActions.resetOrientation()` is used. See: canvas.pathActions.
* @returns {DOMRect|false} The resulting path's bounding box object.
*/
export const getBBoxOfElementAsPath = function (elem: SVGGraphicsElement, addSVGElementFromJson, pathActions): DOMRect | boolean {
    const path = addSVGElementFromJson({
        element: 'path',
        attr: getExtraAttributesForConvertToPath(elem)
    });

    const eltrans = elem.getAttribute('transform');
    if (eltrans) {
        path.setAttribute('transform', eltrans);
    }

    const { parentNode } = elem;
    if (elem.nextSibling) {
        elem.before(path);
    } else {
        parentNode.append(path);
    }

    const d = getPathDFromElement(elem);
    if (d) {
        path.setAttribute('d', d);
    } else {
        path.remove();
    }

    // Get the correct BBox of the new path, then discard it
    pathActions.resetOrientation(path);
    let bb: DOMRect | boolean = false;
    try {
        bb = path.getBBox();
    } catch (e) {
        // Firefox fails
    }
    path.remove();
    return bb;
};

/**
* Convert selected element to a path.
* @function module:utilities.convertToPath
* @param {SVGGraphicsElement} elem - The DOM element to be converted
* @param {module:utilities.SVGElementJSON} attrs - Apply attributes to new path. see canvas.convertToPath
* @param {module:utilities.EditorContext#addSVGElementFromJson} addSVGElementFromJson - Function to add the path element to the current layer. See canvas.addSVGElementFromJson
* @param {module:path.pathActions} pathActions - If a transform exists, pathActions.resetOrientation() is used. See: canvas.pathActions.
* @param {module:draw.DrawCanvasInit#clearSelection|module:path.EditorContext#clearSelection} clearSelection - see [canvas.clearSelection]{@link module:svgcanvas.SvgCanvas#clearSelection}
* @param {module:path.EditorContext#addToSelection} addToSelection - see [canvas.addToSelection]{@link module:svgcanvas.SvgCanvas#addToSelection}
* @param {module:history} hstry - see history module
* @param {module:path.EditorContext#addCommandToHistory|module:draw.DrawCanvasInit#addCommandToHistory} addCommandToHistory - see [canvas.addCommandToHistory]{@link module:svgcanvas~addCommandToHistory}
* @returns {SVGPathElement|null} The converted path element or null if the DOM element was not recognized.
*/
export const convertToPath = function (
    elem: SVGGraphicsElement, attrs, addSVGElementFromJson, pathActions,
    clearSelection, addToSelection, hstry, addCommandToHistory
) {
    const batchCmd = new BatchCommand('Convert element to Path');

    // Any attribute on the element not covered by the passed-in attributes
    attrs = $.extend({}, attrs, getExtraAttributesForConvertToPath(elem));

    const path = addSVGElementFromJson({
        element: 'path',
        attr: attrs
    });

    const eltrans = elem.getAttribute('transform');
    if (eltrans) {
        path.setAttribute('transform', eltrans);
    }

    const { id } = elem;
    const { parentNode } = elem;
    if (elem.nextSibling) {
        elem.before(path);
    } else {
        parentNode.append(path);
    }

    const d = getPathDFromElement(elem);
    if (d) {
        path.setAttribute('d', d);

        // Replace the current element with the converted one

        // Reorient if it has a matrix
        if (eltrans) {
            const tlist = getTransformList(path);
            if (hasMatrixTransform(tlist)) {
                pathActions.resetOrientation(path);
            }
        }

        const { nextSibling } = elem;
        batchCmd.addSubCommand(new RemoveElementCommand(elem, nextSibling, parentNode as Element));
        batchCmd.addSubCommand(new InsertElementCommand(path));

        clearSelection();
        elem.remove();
        path.setAttribute('id', id);
        path.removeAttribute('visibility');
        addToSelection([path], true);

        addCommandToHistory(batchCmd);

        return path;
    }
    // the elem.tagName was not recognized, so no "d" attribute. Remove it, so we've haven't changed anything.
    path.remove();
    return null;
};

/**
* Can the bbox be optimized over the native getBBox? The optimized bbox is the same as the native getBBox when
* the rotation angle is a multiple of 90 degrees and there are no complex transforms.
* Getting an optimized bbox can be dramatically slower, so we want to make sure it's worth it.
*
* The best example for this is a circle rotate 45 degrees. The circle doesn't get wider or taller when rotated
* about it's center.
*
* The standard, unoptimized technique gets the native bbox of the circle, rotates the box 45 degrees, uses
* that width and height, and applies any transforms to get the final bbox. This means the calculated bbox
* is much wider than the original circle. If the angle had been 0, 90, 180, etc. both techniques render the
* same bbox.
*
* The optimization is not needed if the rotation is a multiple 90 degrees. The default technique is to call
* getBBox then apply the angle and any transforms.
*
* @param {Float} angle - The rotation angle in degrees
* @param {boolean} hasAMatrixTransform - True if there is a matrix transform
* @returns {boolean} True if the bbox can be optimized.
*/
function bBoxCanBeOptimizedOverNativeGetBBox(angle: number, hasAMatrixTransform: boolean) {
    const angleModulo90 = angle % 90;
    const closeTo90 = angleModulo90 < -89.99 || angleModulo90 > 89.99;
    const closeTo0 = angleModulo90 > -0.001 && angleModulo90 < 0.001;
    return hasAMatrixTransform || !(closeTo0 || closeTo90);
}

/**
* Get bounding box that includes any transforms.
* @function module:utilities.getBBoxWithTransform
* @param {SVGGraphicsElement} elem - The DOM element to be converted
* @param {module:utilities.EditorContext#addSVGElementFromJson} addSVGElementFromJson - Function to add the path element to the current layer. See canvas.addSVGElementFromJson
* @param {module:path.pathActions} pathActions - If a transform exists, pathActions.resetOrientation() is used. See: canvas.pathActions.
* @returns {module:utilities.BBoxObject|module:math.TransformedBox|DOMRect} A single bounding box object
*/
export const getBBoxWithTransform = function (elem: SVGGraphicsElement, addSVGElementFromJson, pathActions) {
    // TODO: Fix issue with rotated groups. Currently they work
    // fine in FF, but not in other browsers (same problem mentioned
    // in Issue 339 comment #2).

    let bb: BBoxObject = getBBox(elem);

    if (!bb) {
        return null;
    }

    const tlist = getTransformList(elem);
    const angle = getRotationAngleFromTransformList(tlist);
    const hasMatrixXForm = hasMatrixTransform(tlist);

    if (angle || hasMatrixXForm) {
        let goodBb: DOMRect | boolean = false;
        if (bBoxCanBeOptimizedOverNativeGetBBox(angle, hasMatrixXForm)) {
            // Get the BBox from the raw path for these elements
            // TODO: why ellipse and not circle
            const elemNames = ['ellipse', 'path', 'line', 'polyline', 'polygon'];
            if (elemNames.includes(elem.tagName)) {
                goodBb = getBBoxOfElementAsPath(elem, addSVGElementFromJson, pathActions) as DOMRect;
                bb = goodBb;
            } else if (elem.tagName === 'rect') {
                // Look for radius
                const rx = elem.getAttribute('rx');
                const ry = elem.getAttribute('ry');
                if (rx || ry) {
                    goodBb = getBBoxOfElementAsPath(elem, addSVGElementFromJson, pathActions) as DOMRect;
                    bb = goodBb;
                }
            }
        }

        if (!goodBb) {
            const { matrix } = transformListToTransform(tlist);
            bb = transformBox(bb.x, bb.y, bb.width, bb.height, matrix).aabox;
        }
    }
    return bb;
};

/**
 * @param {Element} elem
 * @returns {Float}
 * @todo This is problematic with large stroke-width and, for example, a single
 * horizontal line. The calculated BBox extends way beyond left and right sides.
 */
function getStrokeOffsetForBBox(elem: Element): number {
    const sw = parseFloat(elem.getAttribute('stroke-width'));
    return (!isNaN(sw) && elem.getAttribute('stroke') !== 'none') ? sw / 2 : 0;
}

/**
 * @typedef {PlainObject} BBox
 * @property {Integer} x The x value
 * @property {Integer} y The y value
 * @property {Float} width
 * @property {Float} height
 */
export interface BBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
* Get the bounding box for one or more stroked and/or transformed elements.
* @function module:utilities.getStrokedBBox
* @param {SVGGraphicsElement[]} elems - Array with DOM elements to check
* @param {module:utilities.EditorContext#addSVGElementFromJson} addSVGElementFromJson - Function to add the path element to the current layer. See canvas.addSVGElementFromJson
* @param {module:path.pathActions} pathActions - If a transform exists, pathActions.resetOrientation() is used. See: canvas.pathActions.
* @returns {module:utilities.BBoxObject|module:math.TransformedBox|DOMRect} A single bounding box object
*/
export const getStrokedBBox = function (elems?: SVGGraphicsElement[], addSVGElementFromJson?, pathActions?) {
    if (!elems?.length) {
        return false;
    }

    let fullBb;
    elems.forEach(function (elem) {
        if (fullBb) { return; }
        if (!elem.parentNode) { return; }
        fullBb = getBBoxWithTransform(elem, addSVGElementFromJson, pathActions);
    });

    // This shouldn't ever happen...
    if (fullBb === undefined) { return null; }

    // fullBb doesn't include the stoke, so this does no good!
    // if (elems.length == 1) return fullBb;

    let maxX = fullBb.x + fullBb.width;
    let maxY = fullBb.y + fullBb.height;
    let minX = fullBb.x;
    let minY = fullBb.y;

    // If only one elem, don't call the potentially slow getBBoxWithTransform method again.
    if (elems.length === 1) {
        const offset = getStrokeOffsetForBBox(elems[0]);
        minX -= offset;
        minY -= offset;
        maxX += offset;
        maxY += offset;
    } else {
        elems.forEach(function (elem) {
            const curBb = getBBoxWithTransform(elem, addSVGElementFromJson, pathActions);
            if (curBb) {
                const offset = getStrokeOffsetForBBox(elem);
                minX = Math.min(minX, curBb.x - offset);
                minY = Math.min(minY, curBb.y - offset);
                // TODO: The old code had this test for max, but not min. I suspect this test should be for both min and max
                if (elem.nodeType === Node.ELEMENT_NODE) {
                    maxX = Math.max(maxX, curBb.x + curBb.width + offset);
                    maxY = Math.max(maxY, curBb.y + curBb.height + offset);
                }
            }
        });
    }

    fullBb.x = minX;
    fullBb.y = minY;
    fullBb.width = maxX - minX;
    fullBb.height = maxY - minY;
    return fullBb;
};

/**
* Get all elements that have a BBox (excludes `<defs>`, `<title>`, etc).
* Note that 0-opacity, off-screen etc elements are still considered "visible"
* for this function.
* @function module:utilities.getVisibleElements
* @param {Element} parentElement - The parent DOM element to search within
* @returns {Element[]} All "visible" elements.
*/
export const getVisibleElements = function (parentElement?: Element | Element[]) {
    if (!parentElement) {
        const svgcontent = svgCanvas.getSVGContent();
        parentElement = svgcontent.children as unknown as Element[]; // Prevent layers from being included
    }

    const contentElems = [];

    if (!(parentElement as any).length) {
        parentElement = [parentElement as Element];
    }

    for (let i = 0; i < (parentElement as Element[]).length; ++i) {
        const childrens = parentElement[i].children;
        if (childrens) {
            Array.prototype.forEach.call(childrens, function (elem) {
                if (elem.getBBox) {
                    contentElems.push(elem);
                }
            });
        }
    }

    return contentElems.reverse();
};

/**
* Get the bounding box for one or more stroked and/or transformed elements.
* @function module:utilities.getStrokedBBoxDefaultVisible
* @param {SVGGraphicsElement[]} elems - Array with DOM elements to check
* @returns {module:utilities.BBoxObject} A single bounding box object
*/
export const getStrokedBBoxDefaultVisible = function (elems?: SVGGraphicsElement[]) {
    if (!elems) {
        elems = getVisibleElements();
    }

    return getStrokedBBox(elems, svgCanvas.addSVGElementFromJson, svgCanvas.pathActions);
};

/**
* Get the rotation angle of the given transform list.
* @function module:utilities.getRotationAngleFromTransformList
* @param {SVGTransformList} tlist - List of transforms
* @param {boolean} [toRad] - When true returns the value in radians rather than degrees
* @returns {Float} The angle in degrees or radians
*/
export const getRotationAngleFromTransformList = function (tlist: SVGTransformList, toRad: boolean = false) {
    if (!tlist) { // <svg> elements have no tlist
        return 0;
    }

    const N = tlist.numberOfItems;

    for (let i = 0; i < N; ++i) {
        const xform = tlist.getItem(i);
        if (xform.type === 4) {
            return toRad ? xform.angle * Math.PI / 180.0 : xform.angle;
        }
    }

    return 0.0;
};

/**
* Get the rotation angle of the given/selected DOM element.
* @function module:utilities.getRotationAngle
* @param {SVGGraphicsElement} [elem] - DOM element to get the angle for. Default to first of selected elements.
* @param {boolean} [toRad=false] - When true returns the value in radians rather than degrees
* @returns {Float} The angle in degrees or radians
*/
export let getRotationAngle = function (elem: SVGGraphicsElement | string, toRad: boolean = false) {
    let selected;
    if (typeof elem === "string") {
        selected = getElem(elem);
    } else {
        selected = (elem || svgCanvas.getSelectedElements()[0]);
    }

    // find the rotation transform (if any) and set it
    const tlist = getTransformList(selected);

    return getRotationAngleFromTransformList(tlist as any, toRad); // TODO: Fix as any
};

/**
* Get the reference element associated with the given attribute value.
* @function module:utilities.getRefElem
* @param {string} attrVal - The attribute value as a string
* @returns {Element} Reference element
*/
export const getRefElem = function (attrVal: string): Element {
    return getElem(getUrlFromAttr(attrVal).substring(1));
};

/**
* Get a DOM element by ID within the SVG root element.
* @function module:utilities.getElem
* @param {string} id - String with the element's new ID
* @returns {?Element}
*/
export function getElem<T extends Element = Element>(id: string) {
    // querySelector lookup
    //return svgroot_.querySelector<T>('#' + id);
    return svgroot_.getElementById(id) as T;
}

/**
* Assigns multiple attributes to an element.
* @function module:utilities.assignAttributes
* @param {Element} elem - DOM element to apply new attribute values to
* @param {PlainObject<string, string>} attrs - Object with attribute keys/values
* @param {Integer} [suspendLength] - Milliseconds to suspend redraw
* @param {boolean} [unitCheck=false] - Boolean to indicate the need to use units.setUnitAttr
* @returns {void}
*/
export const assignAttributes = function (elem: Element, attrs: { [key: string]: string | number }, suspendLength?: number, unitCheck: boolean = false) {
    for (const [key, value] of Object.entries<string | number>(attrs)) {
        let ns: string | null = null;
        if (key.startsWith('xml:')) {
            ns = NS.XML;
        } else if (key.startsWith('xlink:')) {
            ns = NS.XLINK;
        } else if (key.startsWith('zaa:')) {
            ns = NS.ZAA;
        }

        if (isNullish(value)) {
            if (ns) {
                elem.removeAttributeNS(ns, key);
            } else {
                elem.removeAttribute(key);
            }
            continue;
        }
        if (ns) {
            elem.setAttributeNS(ns, key, value.toString());
        } else if (!unitCheck) {
            elem.setAttribute(key, value.toString());
        } else {
            setUnitAttr(elem, key, value.toString());
        }
    }
};

/**
* Remove unneeded (default) attributes, making resulting SVG smaller.
* @function module:utilities.cleanupElement
* @param {Element} element - DOM element to clean up
* @returns {void}
*/
export const cleanupElement = function (element: Element) {
    const defaults = {
        'fill-opacity': 1,
        'stop-opacity': 1,
        opacity: 1,
        stroke: 'none',
        'stroke-dasharray': 'none',
        'stroke-linejoin': 'miter',
        'stroke-linecap': 'butt',
        'stroke-opacity': 1,
        'stroke-width': 1,
        rx: 0,
        ry: 0
    };

    if (element.nodeName === 'ellipse') {
        // Ellipse elements require rx and ry attributes
        delete defaults.rx;
        delete defaults.ry;
    }

    Object.entries(defaults).forEach(([attr, val]) => {
        if (element.getAttribute(attr) === String(val)) {
            element.removeAttribute(attr);
        }
    });
};

/**
* Round value to for snapping.
* @function module:utilities.snapToGrid
* @param {Float} value
* @returns {Integer}
*/
export const snapToGrid = function (value: number): number {
    const unit = svgCanvas.getBaseUnit();
    let stepSize = Number(svgCanvas.getCurrentConfig().snappingStep);

    if (unit !== 'px') {
        const stepMult = getSnappingTypeMap()[unit];
        const dpiMult = 72.0 / getCanvasDpi();
        stepSize *= stepMult * dpiMult;
    }

    value = Math.round(value / stepSize) * stepSize;
    return value;
};

/**
 * Prevents default browser click behaviour on the given element.
 * @function module:utilities.preventClickDefault
 * @param {Element} img - The DOM element to prevent the click on
 * @returns {void}
 */
export const preventClickDefault = function (img: any) { // TODO: Fix as any
    const elements = document.querySelectorAll(img);
    Array.from(elements).forEach(function (element) {
        element.addEventListener('click', function (e) {
            e.preventDefault();
        });
    });
};

/**
 * @callback module:utilities.GetNextIDCallback
 * @returns {string} The ID
 */
export type GetNextIDCallback = () => string;

/**
 * Whether a value is `null` or `undefined`.
 * @param {any} val
 * @returns {boolean}
 */
export const isNullish = (val: any) => {
    return val === null || val === undefined;
};

/**
* Overwrite methods for unit testing.
* @function module:utilities.mock
* @param {PlainObject} mockMethods
* @param {module:utilities.getHref} mockMethods.getHref
* @param {module:utilities.setHref} mockMethods.setHref
* @param {module:utilities.getRotationAngle} mockMethods.getRotationAngle
* @returns {void}
*/
export const mock = ({
    getHref: getHrefUser, setHref: setHrefUser, getRotationAngle: getRotationAngleUser
}) => {
    getHref = getHrefUser;
    setHref = setHrefUser;
    getRotationAngle = getRotationAngleUser;
};

// shortcuts to common DOM functions
export const $id = <T extends Element = HTMLElement>(id) => document.getElementById(id) as unknown as T;
export const $qq = <T extends Element = HTMLElement>(sel) => document.querySelector<T>(sel);
export const $qa = <T extends Element = HTMLElement>(sel) => [...document.querySelectorAll<T>(sel)];

export function CssString(string: string) {
    if (!string) {
        return null;
    }

    const css_json = `{"${string
        .replace(/; /g, '", "')
        .replace(/: /g, '": "')
        .replace(";", "")}"}`;

    const obj = JSON.parse(css_json);

    const keyValues = Object.keys(obj).map((key) => {
        const camelCased = key.replace(/-[a-z]/g, (g) => g[1].toUpperCase());
        return { [camelCased]: obj[key] };
    });
    return Object.assign({}, ...keyValues);
}
